import {
  combineLatest,
  distinctUntilChanged,
  map,
  tap,
  type Observable,
  type ObservedValueOf,
} from 'rxjs';
import { reducedStream, reducer } from '@ardoq/rxbeach';
import type { I18nLocale } from '@ardoq/i18n';
import {
  ViewIds,
  type APIOrganizationUser,
  type ArdoqId,
  type GroupingRule,
} from '@ardoq/api-types';
import type { LoadedGraphWithViewpointMode } from '@ardoq/graph';
import type { GraphComponentViewLegendProps } from '@ardoq/view-legend';
import { usersOperations } from '@ardoq/core';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { presentationMode$ } from 'views/streams/presentationMode$';
import { graphGroupingRules$ } from 'streams/grouping/graphGroupingRules$';
import { orgUsersById$ } from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { isEqual } from 'lodash';
import type { RefObject } from 'react';
import type {
  AqBlocks,
  BlocksGraph,
  BlocksLayoutStyle,
  BlocksNode,
} from 'tabview/visualizations/aq-blocks/types';
import {
  isGroup,
  isNodeSynthetic,
} from 'tabview/visualizations/aq-blocks/util';
import { createFlexibleBlockDiagramExportHandlers } from './exportHandlers';
import {
  buildStyleByModelId,
  type ComponentStyle,
} from 'tabview/assistantShared/buildStyleByModelId';
import type { LabelFormattingFilter } from 'tabview/assistantShared/buildBlocksGraph';
import { buildBlocksGraph } from 'tabview/assistantShared/buildBlocksGraph';
import { toLinksAggregation } from 'tabview/assistantShared/referenceLines';
import {
  EMPTY_LEGEND_PROPS,
  toLegendProps,
} from 'tabview/assistantShared/legend';
import { registerViewPane } from 'tabview/assistantShared/registerViewPane';
import { graphHasGroups } from 'tabview/assistantShared/collapsedGroups';
import {
  FLEXIBLE_BLOCK_DIAGRAM_ZOOM_ACTIONS,
  flexibleBlockDiagramSetReadOnly,
} from './actions';
import { toViewPaneZoomActions } from 'tabview/assistantShared/useZoomActions';
import { isQuickExploreSelectionEnabled$ } from 'canvas/isQuickExploreSelectionEnabled';
import { isPresentationMode } from 'env';
import { createViewpointModeFlexibleBlockDiagramCommands } from './commands';
import { ORIENTATION_TO_BLOCKS } from './consts';
import type {
  FlexibleBlockDiagramCommands,
  FlexibleBlockDiagramViewSettings,
} from './types';

export type FlexibleBlockDiagramViewModel = {
  i18nLocale: I18nLocale;
  /**
   * Refs the view populates and the export handlers read: exporting needs the
   * live `<aq-blocks>` element (to re-render the whole diagram off-screen) and
   * the container (to pick up the legend).
   */
  elementRef: RefObject<AqBlocks | null>;
  containerRef: RefObject<HTMLDivElement | null>;
  /** The block diagram to render, or null when the view holds no components. */
  graph: BlocksGraph | null;
  /** The drawn component modelIds, for resolving which nodes a selection covers. */
  componentIds: ReadonlySet<ArdoqId>;
  /** Drives the aq-blocks layout engine; derived from the view settings. */
  layoutStyle: BlocksLayoutStyle;
  /** Surfaced so viewModifiers$ can build the settings panel. */
  viewSettings: FlexibleBlockDiagramViewSettings;
  isLegendActive: boolean;
  legendProps: GraphComponentViewLegendProps;
  /** Drives the presence of the edit-mode toolbar toggle. */
  isPresentationMode: boolean;
  isReadOnly: boolean;
  commands: FlexibleBlockDiagramCommands;
};

/**
 * The graph-shaped part of the view model. It depends on the scope, the grouping,
 * the formatting and the component style — the last because that resolves each
 * component's representation — but on no other view setting, so changing those
 * does not rebuild the graph (layoutStyle relays out in place; the legend toggle
 * only shows/hides the panel).
 */
type GraphViewModel = Pick<
  FlexibleBlockDiagramViewModel,
  'i18nLocale' | 'graph' | 'componentIds' | 'legendProps'
>;

type ToGraphViewModelArgs = {
  i18nLocale: I18nLocale;
  orgUsersById: Record<ArdoqId, APIOrganizationUser>;
  loadedGraph: LoadedGraphWithViewpointMode;
  groupingRules: GroupingRule[];
  formattingFilters: LabelFormattingFilter[];
  /**
   * Resolves both halves of the setting from one value: the representations the
   * canvas draws, and whether the legend describes them as shapes.
   */
  componentStyle: ComponentStyle;
  /** Which groups are drawn closed. See `isGroupCollapsed` in the shared builder. */
  collapsedGroupIds: readonly string[];
};

export const toGraphViewModel = ({
  i18nLocale,
  orgUsersById,
  loadedGraph,
  groupingRules,
  formattingFilters,
  componentStyle,
  collapsedGroupIds,
}: ToGraphViewModelArgs): GraphViewModel => {
  const { graph, referenceTypes, componentIds } = buildBlocksGraph({
    collapsedGroupIds,
    loadedGraph,
    styleByModelId: buildStyleByModelId(
      loadedGraph.scopeData,
      formattingFilters,
      componentStyle
    ),
    groupingRules,
    formattingFilters,
    formatUserFieldFn: val =>
      usersOperations.formatUserField(orgUsersById, val),
    i18nLocale,
    groupLayoutMode: 'engine',
    linkEdgesToNodes: true,
    viewId: ViewIds.FLEXIBLE_BLOCK_DIAGRAM,
  });

  // Nothing in scope, or the traversal rejected the graph model and returned an
  // empty result: render the empty state rather than a blank canvas and a legend.
  if (!graph.root.children?.length) {
    return {
      i18nLocale,
      graph: null,
      componentIds: new Set<ArdoqId>(),
      legendProps: EMPTY_LEGEND_PROPS,
    };
  }

  return {
    i18nLocale,
    graph,
    componentIds: new Set(componentIds),
    legendProps: toLegendProps({
      scopeComponentIds: componentIds,
      referenceTypes,
      displayShapesInsteadOfIcons: componentStyle === 'shape',
    }),
  };
};

export const toLayoutStyle = (
  viewSettings: FlexibleBlockDiagramViewSettings
): BlocksLayoutStyle => ({
  type: viewSettings.layoutType ?? 'process',
  // Fall back on the lookup *result*: a viewpoint persisted before this change
  // may carry an out-of-enum orientation that isn't a key here, yielding
  // undefined (ARD-33247).
  orientation:
    ORIENTATION_TO_BLOCKS[viewSettings.layoutOrientation] ?? 'left-to-right',
  linksAggregation: toLinksAggregation(viewSettings),
});

/**
 * Every non-synthetic group node in the graph, at any depth. Excludes the
 * synthetic root, which is not a user-facing collapsible group.
 */
export const collectGroupNodes = (graph: BlocksGraph): BlocksNode[] => {
  const groups: BlocksNode[] = [];
  const visit = (node: BlocksNode): void => {
    if (!isGroup(node)) return;
    if (!isNodeSynthetic(node)) groups.push(node);
    node.children.forEach(visit);
  };
  visit(graph.root);
  return groups;
};

/** Group id to display label, for exposing groups to the AI by name. */
export const graphGroupLabels = (
  graph: BlocksGraph | null
): Record<string, string> =>
  graph === null
    ? {}
    : Object.fromEntries(
        collectGroupNodes(graph).map(node => [
          node.id,
          node.labels[0] ?? node.id,
        ])
      );

/** What the graph is built from, in the order `graphViewModel$` combines them. */
export type GraphInput = [
  ObservedValueOf<typeof i18nLocale$>,
  ObservedValueOf<typeof loadedGraph$>,
  GroupingRule[],
  ObservedValueOf<typeof formattingAndLabelRules$>,
  ComponentStyle,
  ObservedValueOf<typeof orgUsersById$>,
  readonly string[],
];

/**
 * The graph is rebuilt when any of its inputs changes. The loaded graph is compared
 * by its `scopeData` rather than by identity, so a wrapper re-emitted around the same
 * scope data doesn't relayout the diagram under the user.
 *
 * The builder reads more than `scopeData` — `scopeComponentIds` among it — so that
 * comparison is only sufficient because of an invariant elsewhere: both dispatchers
 * of `replaceScopeComponentIds` (`streams/traversals/routines.ts`,
 * `presentation/viewPane/slideLoader.ts`) build a fresh `enhancedScopeData` in the
 * same breath as the new ids. A reducer that produced new ids around a reused
 * `scopeData` would be dropped here.
 *
 * The other five clauses compare the reference a slot handed over rather than its value —
 * `combineLatest` passes the previous one through for slots that did not emit, and the
 * component style is a string, where the two are the same thing. That is only sufficient
 * because every slot dedupes upstream: `i18nLocale$` behind `distinctUntilChanged()`,
 * `formattingAndLabelRules$`, `graphGroupingRules$` and `orgUsersById$` behind
 * `distinctUntilChanged(isEqual)`, and `componentStyle$` here. A slot that projected through
 * a `map` without deduping would hand over a new reference per emission and rebuild the
 * graph whatever the value.
 */
export const isSameGraphInput = (
  [
    prevLocale,
    prevLoadedGraph,
    prevGroupingRules,
    prevFormatting,
    prevStyle,
    prevOrgUsersById,
  ]: GraphInput,
  [
    locale,
    loadedGraph,
    groupingRules,
    formatting,
    style,
    orgUsersById,
  ]: GraphInput
): boolean =>
  prevLocale === locale &&
  prevLoadedGraph.scopeData === loadedGraph.scopeData &&
  prevGroupingRules === groupingRules &&
  prevFormatting === formatting &&
  prevStyle === style &&
  prevOrgUsersById === orgUsersById;

const VIEW_ID = ViewIds.FLEXIBLE_BLOCK_DIAGRAM;
const VIEW_INSTANCE_ID = toViewpointModeMainViewInstanceId(VIEW_ID);

const setReadOnly = (_: boolean, isReadOnly: boolean) => isReadOnly;

export const getViewModel$ = (): Observable<FlexibleBlockDiagramViewModel> => {
  const elementRef: RefObject<AqBlocks | null> = { current: null };
  const containerRef: RefObject<HTMLDivElement | null> = { current: null };
  const commands$ = isQuickExploreSelectionEnabled$.pipe(
    map(isQuickExploreSelectionEnabled =>
      createViewpointModeFlexibleBlockDiagramCommands({
        isPresentationMode: isPresentationMode(),
        isQuickExploreSelectionEnabled,
        viewInstanceId: VIEW_INSTANCE_ID,
      })
    )
  );

  /** Not a view setting: resets to read-only whenever the view unmounts. */
  const isReadOnly$ = reducedStream(
    'flexibleBlockDiagramIsReadOnly$',
    true,
    [reducer(flexibleBlockDiagramSetReadOnly, setReadOnly)],
    { namespace: VIEW_INSTANCE_ID }
  );

  const exportHandlers = createFlexibleBlockDiagramExportHandlers({
    elementRef,
    containerRef,
  });

  const viewSettings$ = getViewpointModeViewSettingsWithChanges$(
    VIEW_ID,
    VIEW_INSTANCE_ID
  ).pipe(
    map(({ currentSettings }) => currentSettings),
    distinctUntilChanged()
  );

  // The two view settings the graph itself depends on. The component style resolves each
  // component's representation; the collapsed groups decide which groups are built closed. Both
  // have to rebuild the graph, which is what makes them inputs rather than something applied to a
  // graph already on screen.
  const componentStyle$ = viewSettings$.pipe(
    map(({ componentStyle }) => componentStyle),
    distinctUntilChanged()
  );

  /* Supplies the value the graph is built from, and is deliberately absent from
     `isSameGraphInput`: a change to it must not rebuild the graph. The toolbar's action is what
     reaches the groups on screen, so rebuilding as well would lay the whole diagram out twice for
     one click - and on Expand all, twice over the fully expanded graph. What the setting still has
     to do is be read whenever the graph *is* built for some other reason, which is how a stored
     collapse-all is restored.

     Deduped by value all the same, so a settings emission that changed something else entirely
     does not hand over a new reference for the next rebuild to read. */
  const collapsedGroupIds$ = viewSettings$.pipe(
    map(({ collapsedGroupIds }) => collapsedGroupIds),
    distinctUntilChanged<readonly string[]>(isEqual)
  );

  const graphViewModel$: Observable<GraphViewModel> = combineLatest([
    i18nLocale$,
    loadedGraph$,
    graphGroupingRules$,
    formattingAndLabelRules$,
    componentStyle$,
    orgUsersById$,
    collapsedGroupIds$,
  ]).pipe(
    distinctUntilChanged(isSameGraphInput),
    map(
      ([
        i18nLocale,
        loadedGraph,
        groupingRules,
        formattingRules,
        componentStyle,
        orgUsersById,
        collapsedGroupIds,
      ]) =>
        toGraphViewModel({
          i18nLocale,
          orgUsersById,
          loadedGraph,
          groupingRules,
          formattingFilters: formattingRules.rules,
          componentStyle,
          collapsedGroupIds,
        })
    )
  );

  return combineLatest([
    graphViewModel$,
    viewSettings$,
    presentationMode$,
    isReadOnly$,
    commands$,
  ]).pipe(
    map(
      ([
        graphViewModel,
        viewSettings,
        { isPresentationMode },
        isReadOnly,
        commands,
      ]) => ({
        ...graphViewModel,
        elementRef,
        containerRef,
        layoutStyle: toLayoutStyle(viewSettings),
        viewSettings,
        isLegendActive: viewSettings.isLegendActive,
        isPresentationMode,
        isReadOnly,
        commands,
      })
    ),
    tap(viewModel =>
      registerViewPane({
        viewId: VIEW_ID,
        viewProperties: {
          viewSettings: viewModel.viewSettings,
          isPresentationMode: viewModel.isPresentationMode,
          isReadOnly: viewModel.isReadOnly,
          commands: viewModel.commands,
          hasGroups: graphHasGroups(viewModel.graph),
          isEmptyView: viewModel.graph === null,
          exportHandlers,
          viewInstanceId: VIEW_INSTANCE_ID,
          groups: graphGroupLabels(viewModel.graph),
        },
        isLegendActive: viewModel.isLegendActive,
        getContainer: exportHandlers.getContainer,
        viewInstanceId: VIEW_INSTANCE_ID,
        ...toViewPaneZoomActions(FLEXIBLE_BLOCK_DIAGRAM_ZOOM_ACTIONS),
      })
    )
  );
};
